/**
 * FileList component
 * Displays files in a grouped list with collapsible sections
 */

import { useState } from 'react'
import filesService from '../../services/files'
import toast from 'react-hot-toast'

const FileList = ({
  files,
  deletedFiles = [],
  userRole,
  currentUserId,
  onFileDeleted,
  onFileRestored,
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedSections, setExpandedSections] = useState({
    uploaded: true,
    generated: true,
    deleted: false,
  })
  const [downloadingFiles, setDownloadingFiles] = useState(new Set())
  const [deletingFiles, setDeletingFiles] = useState(new Set())
  const [confirmDelete, setConfirmDelete] = useState(null)

  // Filter files by search query
  const filteredFiles = files.filter((file) =>
    file.original_filename?.toLowerCase().includes(searchQuery.toLowerCase())
  )
  const filteredDeletedFiles = deletedFiles.filter((file) =>
    file.original_filename?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // Group files by category
  const uploadedFiles = filteredFiles.filter((f) => f.category === 'uploaded')
  const generatedFiles = filteredFiles.filter((f) => f.category === 'generated')

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }))
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
            Pending
          </span>
        )
      case 'processing':
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 flex items-center gap-1">
            <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Processing
          </span>
        )
      case 'completed':
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
            Ready
          </span>
        )
      case 'failed':
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
            Failed
          </span>
        )
      default:
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
            {status}
          </span>
        )
    }
  }

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
  }

  const formatDate = (dateString) => {
    if (!dateString) return ''
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const handleDownload = async (file) => {
    if (file.status !== 'completed') {
      toast.error('File is not ready for download')
      return
    }

    setDownloadingFiles((prev) => new Set(prev).add(file.id))
    try {
      const response = await filesService.downloadFile(file.id)
      const url = window.URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', file.original_filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      toast.success('File downloaded successfully')
    } catch (error) {
      console.error('Download error:', error)
      toast.error(error.response?.data?.detail || 'Failed to download file')
    } finally {
      setDownloadingFiles((prev) => {
        const updated = new Set(prev)
        updated.delete(file.id)
        return updated
      })
    }
  }

  const handleDelete = async (file) => {
    if (confirmDelete !== file.id) {
      setConfirmDelete(file.id)
      return
    }

    setDeletingFiles((prev) => new Set(prev).add(file.id))
    setConfirmDelete(null)

    try {
      if (file.is_deleted) {
        // Permanent delete
        await filesService.permanentlyDeleteFile(file.id)
        toast.success('File permanently deleted')
      } else {
        // Soft delete
        await filesService.deleteFile(file.id)
        toast.success('File moved to trash')
      }
      onFileDeleted(file.id)
    } catch (error) {
      console.error('Delete error:', error)
      toast.error(error.response?.data?.detail || 'Failed to delete file')
    } finally {
      setDeletingFiles((prev) => {
        const updated = new Set(prev)
        updated.delete(file.id)
        return updated
      })
    }
  }

  const handleRestore = async (file) => {
    try {
      const restoredFile = await filesService.restoreFile(file.id)
      toast.success('File restored successfully')
      onFileRestored(restoredFile)
    } catch (error) {
      console.error('Restore error:', error)
      toast.error(error.response?.data?.detail || 'Failed to restore file')
    }
  }

  const canDelete = (file) => {
    if (userRole === 'owner') return true
    if (userRole === 'editor' && file.uploaded_by === currentUserId) return true
    return false
  }

  const renderFileRow = (file) => {
    const isDownloading = downloadingFiles.has(file.id)
    const isDeleting = deletingFiles.has(file.id)
    const showConfirm = confirmDelete === file.id
    const canDownload = file.status === 'completed'
    const showDelete = canDelete(file) && !file.is_deleted

    return (
      <div
        key={file.id}
        className="flex items-center gap-3 py-3 px-4 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
      >
        {/* File icon and name */}
        <div className="flex-1 min-w-0 flex items-center gap-3">
          <div className="flex-shrink-0">
            <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{file.original_filename}</p>
            {file.error_message && (
              <p className="text-xs text-red-600 mt-0.5 truncate">{file.error_message}</p>
            )}
          </div>
        </div>

        {/* File size */}
        <div className="flex-shrink-0 w-20 text-right">
          <span className="text-sm text-gray-600">{formatFileSize(file.file_size)}</span>
        </div>

        {/* Status */}
        <div className="flex-shrink-0 w-24">{getStatusBadge(file.status)}</div>

        {/* Upload date */}
        <div className="flex-shrink-0 w-24 text-right">
          <span className="text-sm text-gray-600">{formatDate(file.created_at)}</span>
        </div>

        {/* Actions */}
        <div className="flex-shrink-0 flex items-center gap-2">
          {/* Download button */}
          <button
            onClick={() => handleDownload(file)}
            disabled={!canDownload || isDownloading}
            className={`p-2 rounded transition-colors ${
              canDownload
                ? 'text-gray-600 hover:text-primary-600 hover:bg-primary-50'
                : 'text-gray-300 cursor-not-allowed'
            }`}
            title={canDownload ? 'Download file' : 'File not ready'}
          >
            {isDownloading ? (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
            )}
          </button>

          {/* Delete/Restore button */}
          {file.is_deleted ? (
            <button
              onClick={() => handleRestore(file)}
              className="p-2 rounded text-green-600 hover:text-green-700 hover:bg-green-50 transition-colors"
              title="Restore file"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          ) : showDelete ? (
            showConfirm ? (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleDelete(file)}
                  disabled={isDeleting}
                  className="px-2 py-1 text-xs font-medium text-red-600 hover:text-red-700 border border-red-300 rounded hover:bg-red-50"
                >
                  {isDeleting ? 'Deleting...' : 'Confirm'}
                </button>
                <button
                  onClick={() => setConfirmDelete(null)}
                  className="px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => handleDelete(file)}
                className="p-2 rounded text-gray-600 hover:text-red-600 hover:bg-red-50 transition-colors"
                title="Delete file"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            )
          ) : (
            <div className="w-8" /> // Spacer to maintain alignment
          )}
        </div>
      </div>
    )
  }

  const renderSection = (title, files, sectionKey, emptyMessage) => {
    const isExpanded = expandedSections[sectionKey]

    return (
      <div className="mb-4">
        <button
          onClick={() => toggleSection(sectionKey)}
          className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200"
        >
          <div className="flex items-center gap-2">
            <svg
              className={`w-4 h-4 text-gray-600 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <h3 className="text-sm font-semibold text-gray-900">
              {title} <span className="text-gray-500 font-normal">({files.length})</span>
            </h3>
          </div>
        </button>

        {isExpanded && (
          <div className="mt-2 bg-white rounded-lg border border-gray-200 overflow-hidden">
            {files.length > 0 ? (
              <div>{files.map(renderFileRow)}</div>
            ) : (
              <div className="py-8 text-center text-sm text-gray-500">{emptyMessage}</div>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div>
      {/* Search bar */}
      <div className="mb-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg
              className="h-5 w-5 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search files..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <svg
                className="h-5 w-5 text-gray-400 hover:text-gray-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* File sections */}
      {renderSection('Input Files', uploadedFiles, 'uploaded', 'No input files uploaded yet')}
      {renderSection(
        'Generated Files',
        generatedFiles,
        'generated',
        'No generated files yet. Run an analysis to create output files.'
      )}

      {/* Deleted Files Section - only show if there are deleted files */}
      {filteredDeletedFiles.length > 0 && (
        <div className="mb-4">
          <button
            onClick={() => toggleSection('deleted')}
            className="w-full flex items-center justify-between p-3 bg-red-50 hover:bg-red-100 rounded-lg transition-colors border border-red-200"
          >
            <div className="flex items-center gap-2">
              <svg
                className={`w-4 h-4 text-red-600 transition-transform ${expandedSections.deleted ? 'rotate-90' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
              <svg
                className="w-4 h-4 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              <h3 className="text-sm font-semibold text-gray-900">
                Deleted Files{' '}
                <span className="text-gray-500 font-normal">({filteredDeletedFiles.length})</span>
              </h3>
              <span className="text-xs text-red-600 font-normal ml-2">
                Will be permanently deleted after 30 days
              </span>
            </div>
          </button>

          {expandedSections.deleted && (
            <div className="mt-2 bg-white rounded-lg border border-red-200 overflow-hidden">
              {filteredDeletedFiles.length > 0 ? (
                <div>{filteredDeletedFiles.map(renderFileRow)}</div>
              ) : (
                <div className="py-8 text-center text-sm text-gray-500">No deleted files</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default FileList
